import React from "react";

import {
    Alert,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
    Typography,
} from "antd";

import {
    DeleteOutlined,
    DeploymentUnitOutlined,
    ExperimentOutlined,
    FunctionOutlined,
    LineChartOutlined,
} from "@ant-design/icons";

import StructuredContentRenderer from "./StructuredContentRenderer";

const {
    TextArea,
} = Input;

const {
    Title,
} = Typography;

const SHAPE_TYPES = [
    {
        value: "triangle",
        label: "Triangle",
    },
    {
        value: "rectangle",
        label: "Rectangle",
    },
    {
        value: "square",
        label: "Square",
    },
    {
        value: "circle",
        label: "Circle",
    },
    {
        value: "ellipse",
        label: "Ellipse",
    },
    {
        value: "line",
        label: "Line",
    },
    {
        value: "angle",
        label: "Angle",
    },
    {
        value: "parallel_lines",
        label: "Parallel Lines",
    },
    {
        value: "perpendicular_lines",
        label: "Perpendicular Lines",
    },
];

const COLORS = {
    orange: "#f97316",
    black: "#111111",
    camel: "#c19a6b",
    cream: "#fffaf3",
    white: "#ffffff",
    border: "#e5d4c0",
};

const addBlock = (
    question,
    updateQuestion,
    block
) => {
    const payload =
        question.structured_payload || {
            blocks: [],
        };

    updateQuestion(
        "structured_payload",
        {
            ...payload,
            blocks: [
                ...(payload.blocks || []),
                block,
            ],
        }
    );
};

const addMathBlock = (
    question,
    updateQuestion
) => {
    addBlock(
        question,
        updateQuestion,
        {
            type: "math",
            latex: "",
        }
    );
};

const addFractionBlock = (
    question,
    updateQuestion
) => {
    addBlock(
        question,
        updateQuestion,
        {
            type: "fraction",
            numerator: "",
            denominator: "",
        }
    );
};

const addChemistryBlock = (
    question,
    updateQuestion
) => {
    addBlock(
        question,
        updateQuestion,
        {
            type: "chemistry",
            formula: "",
        }
    );
};

const addGraphBlock = (
    question,
    updateQuestion
) => {
    addBlock(
        question,
        updateQuestion,
        {
            type: "graph",
            graph_type: "line",
            title: "",
            x_axis: "",
            y_axis: "",
            data: [],
            data_text: "",
        }
    );
};

const addShapeBlock = (
    question,
    updateQuestion
) => {
    addBlock(
        question,
        updateQuestion,
        {
            type: "shape",

            shape_type: "triangle",

            width: 320,

            height: 220,

            rotation: 0,

            stroke: "#111111",

            stroke_width: 3,

            fill: "transparent",

            labels: {
                top: "",
                left: "",
                right: "",
                bottom: "",
                center: "",
            },

            dimensions: {
                top: "",
                left: "",
                right: "",
                bottom: "",
            },

            angle: 60,

            angle_label: "",

            radius: "",

            diameter: "",
        }
    );
};

const ShapeEditor = ({
    block,
    blockIndex,
    updateBlock,
}) => {
    const updateNestedValue = (
        parent,
        field,
        value
    ) => {
        updateBlock(
            blockIndex,
            parent,
            {
                ...(block[parent] || {}),
                [field]: value,
            }
        );
    };

    return (
        <Space
            direction="vertical"
            size="middle"
            style={{
                width: "100%",
            }}
        >
            <Form.Item label="Geometric Shape">
                <Select
                    value={
                        block.shape_type ||
                        "triangle"
                    }
                    options={SHAPE_TYPES}
                    onChange={(value) =>
                        updateBlock(
                            blockIndex,
                            "shape_type",
                            value
                        )
                    }
                />
            </Form.Item>

            <Row
                gutter={[
                    16,
                    16,
                ]}
            >
                <Col
                    xs={24}
                    sm={8}
                >
                    <Form.Item label="Width">
                        <InputNumber
                            min={50}
                            max={1200}
                            style={{
                                width: "100%",
                            }}
                            value={
                                block.width
                            }
                            onChange={(value) =>
                                updateBlock(
                                    blockIndex,
                                    "width",
                                    value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={8}
                >
                    <Form.Item label="Height">
                        <InputNumber
                            min={50}
                            max={1200}
                            style={{
                                width: "100%",
                            }}
                            value={
                                block.height
                            }
                            onChange={(value) =>
                                updateBlock(
                                    blockIndex,
                                    "height",
                                    value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={8}
                >
                    <Form.Item label="Rotation">
                        <InputNumber
                            min={-360}
                            max={360}
                            style={{
                                width: "100%",
                            }}
                            value={
                                block.rotation ||
                                0
                            }
                            onChange={(value) =>
                                updateBlock(
                                    blockIndex,
                                    "rotation",
                                    value || 0
                                )
                            }
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row
                gutter={[
                    16,
                    16,
                ]}
            >
                <Col
                    xs={24}
                    sm={8}
                >
                    <Form.Item label="Stroke Width">
                        <InputNumber
                            min={1}
                            max={20}
                            style={{
                                width: "100%",
                            }}
                            value={
                                block.stroke_width ||
                                3
                            }
                            onChange={(value) =>
                                updateBlock(
                                    blockIndex,
                                    "stroke_width",
                                    value || 1
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={8}
                >
                    <Form.Item label="Stroke Color">
                        <Input
                            type="color"
                            value={
                                block.stroke ||
                                "#111111"
                            }
                            onChange={(event) =>
                                updateBlock(
                                    blockIndex,
                                    "stroke",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={8}
                >
                    <Form.Item label="Fill Color">
                        <Input
                            type="color"
                            value={
                                block.fill ===
                                "transparent"
                                    ? "#ffffff"
                                    : block.fill ||
                                      "#ffffff"
                            }
                            onChange={(event) =>
                                updateBlock(
                                    blockIndex,
                                    "fill",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider />

            <Title level={5}>
                Labels
            </Title>

            <Row
                gutter={[
                    16,
                    16,
                ]}
            >
                <Col
                    xs={24}
                    sm={12}
                >
                    <Form.Item label="Top Label">
                        <Input
                            placeholder="Example: A"
                            value={
                                block.labels?.top ||
                                ""
                            }
                            onChange={(event) =>
                                updateNestedValue(
                                    "labels",
                                    "top",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Form.Item label="Left Label">
                        <Input
                            placeholder="Example: B"
                            value={
                                block.labels?.left ||
                                ""
                            }
                            onChange={(event) =>
                                updateNestedValue(
                                    "labels",
                                    "left",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Form.Item label="Right Label">
                        <Input
                            placeholder="Example: C"
                            value={
                                block.labels?.right ||
                                ""
                            }
                            onChange={(event) =>
                                updateNestedValue(
                                    "labels",
                                    "right",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Form.Item label="Bottom Label">
                        <Input
                            placeholder="Example: D"
                            value={
                                block.labels?.bottom ||
                                ""
                            }
                            onChange={(event) =>
                                updateNestedValue(
                                    "labels",
                                    "bottom",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Form.Item label="Center Label">
                        <Input
                            placeholder="Example: O"
                            value={
                                block.labels?.center ||
                                ""
                            }
                            onChange={(event) =>
                                updateNestedValue(
                                    "labels",
                                    "center",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider />

            <Title level={5}>
                Measurements
            </Title>

            <Row
                gutter={[
                    16,
                    16,
                ]}
            >
                <Col
                    xs={24}
                    sm={12}
                >
                    <Form.Item label="Top Measurement">
                        <Input
                            placeholder="Example: 5 cm"
                            value={
                                block.dimensions?.top ||
                                ""
                            }
                            onChange={(event) =>
                                updateNestedValue(
                                    "dimensions",
                                    "top",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Form.Item label="Left Measurement">
                        <Input
                            placeholder="Example: 7 cm"
                            value={
                                block.dimensions?.left ||
                                ""
                            }
                            onChange={(event) =>
                                updateNestedValue(
                                    "dimensions",
                                    "left",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Form.Item label="Right Measurement">
                        <Input
                            placeholder="Example: 8 cm"
                            value={
                                block.dimensions?.right ||
                                ""
                            }
                            onChange={(event) =>
                                updateNestedValue(
                                    "dimensions",
                                    "right",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Form.Item label="Bottom Measurement">
                        <Input
                            placeholder="Example: 10 cm"
                            value={
                                block.dimensions?.bottom ||
                                ""
                            }
                            onChange={(event) =>
                                updateNestedValue(
                                    "dimensions",
                                    "bottom",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </Col>
            </Row>

            {block.shape_type ===
                "angle" && (
                <>
                    <Divider />

                    <Title level={5}>
                        Angle Settings
                    </Title>

                    <Form.Item label="Angle Value">
                        <InputNumber
                            min={1}
                            max={359}
                            style={{
                                width: "100%",
                            }}
                            value={
                                block.angle ||
                                60
                            }
                            onChange={(value) =>
                                updateBlock(
                                    blockIndex,
                                    "angle",
                                    value || 1
                                )
                            }
                        />
                    </Form.Item>

                    <Form.Item label="Angle Label">
                        <Input
                            placeholder="Example: 60°"
                            value={
                                block.angle_label ||
                                ""
                            }
                            onChange={(event) =>
                                updateBlock(
                                    blockIndex,
                                    "angle_label",
                                    event.target.value
                                )
                            }
                        />
                    </Form.Item>
                </>
            )}

            {block.shape_type ===
                "circle" && (
                <>
                    <Divider />

                    <Title level={5}>
                        Circle Measurements
                    </Title>

                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                    >
                        <Col
                            xs={24}
                            sm={12}
                        >
                            <Form.Item label="Radius">
                                <Input
                                    placeholder="Example: 5 cm"
                                    value={
                                        block.radius ||
                                        ""
                                    }
                                    onChange={(event) =>
                                        updateBlock(
                                            blockIndex,
                                            "radius",
                                            event.target.value
                                        )
                                    }
                                />
                            </Form.Item>
                        </Col>

                        <Col
                            xs={24}
                            sm={12}
                        >
                            <Form.Item label="Diameter">
                                <Input
                                    placeholder="Example: 10 cm"
                                    value={
                                        block.diameter ||
                                        ""
                                    }
                                    onChange={(event) =>
                                        updateBlock(
                                            blockIndex,
                                            "diameter",
                                            event.target.value
                                        )
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            )}
        </Space>
    );
};

const StructuredContentEditor = ({
    question,
    updateQuestion,
}) => {
    const payload =
        question.structured_payload || {
            blocks: [],
        };

    const blocks =
        payload.blocks || [];

    const updateBlock = (
        blockIndex,
        field,
        value
    ) => {
        const updatedBlocks =
            blocks.map(
                (
                    block,
                    index
                ) =>
                    index ===
                    blockIndex
                        ? {
                              ...block,
                              [field]:
                                  value,
                          }
                        : block
            );

        updateQuestion(
            "structured_payload",
            {
                ...payload,
                blocks:
                    updatedBlocks,
            }
        );
    };

    const deleteBlock = (
        blockIndex
    ) => {
        updateQuestion(
            "structured_payload",
            {
                ...payload,
                blocks:
                    blocks.filter(
                        (
                            _,
                            index
                        ) =>
                            index !==
                            blockIndex
                    ),
            }
        );
    };

    return (
        <Card
            size="small"
            title="Structured Content"
            style={{
                marginTop: 16,
                background:
                    COLORS.cream,
                borderColor:
                    COLORS.border,
            }}
        >
            <Space wrap>
                <Button
                    icon={
                        <FunctionOutlined />
                    }
                    onClick={() =>
                        addMathBlock(
                            question,
                            updateQuestion
                        )
                    }
                >
                    Math Formula
                </Button>

                <Button
                    onClick={() =>
                        addFractionBlock(
                            question,
                            updateQuestion
                        )
                    }
                >
                    Fraction
                </Button>

                <Button
                    icon={
                        <ExperimentOutlined />
                    }
                    onClick={() =>
                        addChemistryBlock(
                            question,
                            updateQuestion
                        )
                    }
                >
                    Chemistry
                </Button>

                <Button
                    icon={
                        <LineChartOutlined />
                    }
                    onClick={() =>
                        addGraphBlock(
                            question,
                            updateQuestion
                        )
                    }
                >
                    Graph
                </Button>

                <Button
                    icon={
                        <DeploymentUnitOutlined />
                    }
                    onClick={() =>
                        addShapeBlock(
                            question,
                            updateQuestion
                        )
                    }
                >
                    Geometric Shape
                </Button>
            </Space>

            {blocks.length ===
                0 && (
                <Alert
                    style={{
                        marginTop: 16,
                    }}
                    type="info"
                    message="No structured content added"
                    description="Add a mathematical formula, fraction, chemistry formula, graph, or geometric shape."
                />
            )}

            {blocks.map(
                (
                    block,
                    blockIndex
                ) => (
                    <Card
                        key={
                            blockIndex
                        }
                        size="small"
                        style={{
                            marginTop: 16,
                            borderColor:
                                COLORS.border,
                        }}
                        title={
                            block.type ===
                            "math"
                                ? "Mathematical Formula"
                                : block.type ===
                                  "fraction"
                                ? "Fraction"
                                : block.type ===
                                  "chemistry"
                                ? "Chemistry Formula"
                                : block.type ===
                                  "graph"
                                ? "Graph"
                                : "Geometric Shape"
                        }
                        extra={
                            <Button
                                danger
                                type="text"
                                icon={
                                    <DeleteOutlined />
                                }
                                onClick={() =>
                                    deleteBlock(
                                        blockIndex
                                    )
                                }
                            />
                        }
                    >
                        {block.type ===
                            "math" && (
                            <Form.Item
                                label="LaTeX Formula"
                                help="Examples: x^2 + 5x + 6 = 0, \\frac{1}{2}, \\sqrt{x}"
                            >
                                <TextArea
                                    rows={
                                        3
                                    }
                                    placeholder="Enter mathematical formula"
                                    value={
                                        block.latex ||
                                        ""
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateBlock(
                                            blockIndex,
                                            "latex",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </Form.Item>
                        )}

                        {block.type ===
                            "fraction" && (
                            <Row
                                gutter={[
                                    20,
                                    20,
                                ]}
                            >
                                <Col
                                    xs={
                                        24
                                    }
                                    sm={
                                        12
                                    }
                                >
                                    <Form.Item label="Numerator">
                                        <Input
                                            value={
                                                block.numerator ||
                                                ""
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                updateBlock(
                                                    blockIndex,
                                                    "numerator",
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={
                                        24
                                    }
                                    sm={
                                        12
                                    }
                                >
                                    <Form.Item label="Denominator">
                                        <Input
                                            value={
                                                block.denominator ||
                                                ""
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                updateBlock(
                                                    blockIndex,
                                                    "denominator",
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}

                        {block.type ===
                            "chemistry" && (
                            <Form.Item label="Chemical Formula or Equation">
                                <Input
                                    placeholder="Example: H2SO4 or 2H2 + O2 -> 2H2O"
                                    value={
                                        block.formula ||
                                        ""
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateBlock(
                                            blockIndex,
                                            "formula",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </Form.Item>
                        )}

                        {block.type ===
                            "graph" && (
                            <Space
                                direction="vertical"
                                size="middle"
                                style={{
                                    width: "100%",
                                }}
                            >
                                <Input
                                    placeholder="Graph Title"
                                    value={
                                        block.title ||
                                        ""
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateBlock(
                                            blockIndex,
                                            "title",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />

                                <Input
                                    placeholder="X-Axis Label"
                                    value={
                                        block.x_axis ||
                                        ""
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateBlock(
                                            blockIndex,
                                            "x_axis",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />

                                <Input
                                    placeholder="Y-Axis Label"
                                    value={
                                        block.y_axis ||
                                        ""
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateBlock(
                                            blockIndex,
                                            "y_axis",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />

                                <TextArea
                                    rows={
                                        5
                                    }
                                    placeholder={`Example:
0,0
1,5
2,10`}
                                    value={
                                        block.data_text ||
                                        ""
                                    }
                                    onChange={(
                                        event
                                    ) => {
                                        const text =
                                            event
                                                .target
                                                .value;

                                        const data =
                                            text
                                                .split(
                                                    "\n"
                                                )
                                                .map(
                                                    (
                                                        line
                                                    ) => {
                                                        const [
                                                            x,
                                                            y,
                                                        ] =
                                                            line.split(
                                                                ","
                                                            );

                                                        return {
                                                            x: Number(
                                                                x
                                                            ),
                                                            y: Number(
                                                                y
                                                            ),
                                                        };
                                                    }
                                                )
                                                .filter(
                                                    (
                                                        point
                                                    ) =>
                                                        Number.isFinite(
                                                            point.x
                                                        ) &&
                                                        Number.isFinite(
                                                            point.y
                                                        )
                                                );

                                        updateQuestion(
                                            "structured_payload",
                                            {
                                                ...payload,
                                                blocks:
                                                    blocks.map(
                                                        (
                                                            currentBlock,
                                                            index
                                                        ) =>
                                                            index ===
                                                            blockIndex
                                                                ? {
                                                                      ...currentBlock,
                                                                      data_text:
                                                                          text,
                                                                      data,
                                                                  }
                                                                : currentBlock
                                                    ),
                                            }
                                        );
                                    }}
                                />
                            </Space>
                        )}

                        {block.type ===
                            "shape" && (
                            <ShapeEditor
                                block={
                                    block
                                }
                                blockIndex={
                                    blockIndex
                                }
                                updateBlock={
                                    updateBlock
                                }
                            />
                        )}

                        <Divider />

                        <Card
                            size="small"
                            title="Live Preview"
                            style={{
                                background:
                                    COLORS.white,
                                borderColor:
                                    COLORS.border,
                               overflow: "visible",
                            }}
                        >
                            <StructuredContentRenderer
                                payload={{
                                    blocks: [
                                        block,
                                    ],
                                }}
                                mode="editor"
                            />
                        </Card>
                    </Card>
                )
            )}
        </Card>
    );
};

export default StructuredContentEditor;
